/**
 * IncidentNotifyConfigCard — admin-only editor for who gets auto-
 * notified when a new incident is filed. Backed by
 * /api/incidents/settings/notify. Rendered on the Admin page next to
 * user management.
 *
 * Design:
 *   - Multi-select from the existing user list (typeahead-lite:
 *     filter by name/email as you type into the picker input).
 *   - Severity threshold slider (low/med/high/critical) so a safety
 *     manager who only wants "the bad ones" can subscribe without
 *     getting near-miss noise.
 *   - Admins always get notified regardless of this config — this
 *     list is for NON-admins who need cross-project safety alerts.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BellRing, X, Plus, Save } from "lucide-react";

const SEV_CHOICES = [
  { value: "low",      label: "Every incident (low+)" },
  { value: "medium",   label: "Medium+ (default)" },
  { value: "high",     label: "High+ only" },
  { value: "critical", label: "Critical only" },
];

export default function IncidentNotifyConfigCard() {
  const [settings, setSettings] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [minSeverity, setMinSeverity] = useState("medium");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    // Two independent fetches — DON'T Promise.all them, because a
    // single .catch would wipe state on either failure, making a
    // successful save look like it reverted after reload. Load them
    // separately so each surface degrades independently.
    api.get("/incidents/settings/notify")
      .then((r) => {
        setSettings(r.data);
        setMinSeverity(r.data.min_severity || "medium");
        setSelectedIds(r.data.user_ids || []);
      })
      .catch(() => setSettings({ user_ids: [], min_severity: "medium", users: [] }));
    api.get("/users")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.users || []);
        setAllUsers(list);
      })
      .catch(() => setAllUsers([]));
  }, []);

  const selectedUsers = useMemo(() => {
    // Prefer names from the fresh /admin/users list; fall back to
    // hydrated names from /incidents/settings/notify (in case a
    // user was deactivated but is still on the list).
    return selectedIds.map((id) => {
      const u = allUsers.find((x) => x.id === id)
        || (settings?.users || []).find((x) => x.id === id)
        || { id, email: id, full_name: "(unknown user)" };
      return u;
    });
  }, [selectedIds, allUsers, settings]);

  const PICKER_LIMIT = 20;
  const { filteredCandidates, capped } = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    const matches = allUsers
      .filter((u) => !selectedIds.includes(u.id))
      .filter((u) => u.is_active !== false)
      .filter((u) => !q
        || (u.email || "").toLowerCase().includes(q)
        || (u.full_name || "").toLowerCase().includes(q));
    return {
      filteredCandidates: matches.slice(0, PICKER_LIMIT),
      capped: matches.length > PICKER_LIMIT,
    };
  }, [allUsers, selectedIds, pickerQuery]);

  const addUser = (u) => {
    setSelectedIds((ids) => [...ids, u.id]);
    setPickerQuery("");
  };
  const removeUser = (id) => {
    setSelectedIds((ids) => ids.filter((x) => x !== id));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/incidents/settings/notify", {
        user_ids: selectedIds,
        min_severity: minSeverity,
      });
      setSettings(r.data);
      toast.success("Notify list saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="incident-notify-config-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2">
          <div className="p-2 rounded-md border border-amber-900 bg-amber-950/40 text-amber-300 shrink-0">
            <BellRing className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">
              Incident Notifications
            </div>
            <div className="font-display font-bold text-lg tracking-tight">Auto-notify list</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Users on this list get an in-app notification when a new incident is filed that meets the severity threshold. Admins are always notified regardless.
            </div>
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1.5">
            Severity threshold
          </Label>
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Severity threshold">
            {SEV_CHOICES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setMinSeverity(s.value)}
                className={`h-7 px-2.5 rounded-full text-[11px] font-display font-bold uppercase tracking-wider border ${
                  minSeverity === s.value
                    ? "bg-blue-700 border-blue-500 text-white"
                    : "border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800"
                }`}
                data-testid={`incident-notify-sev-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold block mb-1.5">
            Subscribed users ({selectedUsers.length})
          </Label>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="incident-notify-selected-users">
            {selectedUsers.length === 0 && (
              <span className="text-xs italic text-slate-500">Only admins will be notified.</span>
            )}
            {selectedUsers.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-full border border-slate-700 bg-slate-950 text-xs"
                data-testid={`incident-notify-user-${u.id}`}
              >
                {u.full_name || u.email}
                <button
                  type="button"
                  onClick={() => removeUser(u.id)}
                  className="text-slate-500 hover:text-red-400"
                  aria-label={`Remove ${u.full_name || u.email}`}
                  data-testid={`incident-notify-remove-${u.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-full border border-dashed border-slate-700 bg-slate-950 text-xs text-slate-400 hover:bg-slate-800"
              data-testid="incident-notify-add-btn"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>

          {pickerOpen && (
            <div className="p-3 rounded-md border border-slate-800 bg-slate-950" data-testid="incident-notify-picker">
              <Input
                autoFocus
                placeholder="Search by name or email…"
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                className="bg-slate-900 border-slate-700 mb-2 text-sm"
                data-testid="incident-notify-picker-input"
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredCandidates.length === 0 ? (
                  <div className="text-xs text-slate-500 italic p-2">No matching users.</div>
                ) : (
                  filteredCandidates.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => addUser(u)}
                      className="w-full text-left flex items-center justify-between p-2 rounded hover:bg-slate-800 border border-transparent hover:border-slate-700 text-sm"
                      data-testid={`incident-notify-candidate-${u.id}`}
                    >
                      <span>
                        <span className="font-display font-bold">{u.full_name || "(no name)"}</span>
                        <span className="text-slate-500 ml-2 text-xs">{u.email}</span>
                      </span>
                      <span className="text-[10px] uppercase text-slate-500">{u.role}</span>
                    </button>
                  ))
                )}
              </div>
              {capped && (
                <div
                  className="text-[11px] text-amber-300/80 italic mt-2 pt-2 border-t border-slate-800"
                  data-testid="incident-notify-picker-cap-hint"
                >
                  Showing first {PICKER_LIMIT}. Refine your search to see more.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-600 font-display tracking-wider" data-testid="incident-notify-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "SAVING…" : "SAVE"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
